import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { VaultsController } from 'networking/controllers/vaults-controller';
import { goToPage, RouteName } from 'routes';
import { Spinner } from 'common/spinner';
import { AboutVault } from 'pages/vault-detail/components/about-vault';
import { AppContext, appActions } from 'context';
import { VaultDetailProvider } from 'context/vault-detail-context';
import { vaultSubPageBreadcrumb } from 'helpers/vault-breadcrumbs';
import { VaultTopBar } from 'pages/vault-detail/components/vault-top-bar';
import { SettingsLayout } from 'common/settings-layout';
import { VaultSettingsNav } from './components/vault-settings-nav';
import styles from './vault-settings.module.scss';

const VaultSettingsContent: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { dispatch } = useContext(AppContext);
  const [vault, setVault] = useState<VaultType | null>(null);
  const [fetching, setFetching] = useState(true);

  const fetchVault = async () => {
    if (!id) return;
    const vaultId = Number(id) || 0;
    if (vaultId <= 0) {
      goToPage(RouteName.Vaults);
      return;
    }

    try {
      setFetching(true);
      const response = await VaultsController.getVault(vaultId, 'summary');
      // Settings are for the owner and editors; others go to the vault.
      if (response.myAccess && !response.myAccess.canManageVault) {
        goToPage(RouteName.VaultDetail, { id: vaultId });
        return;
      }
      setVault(response);
    } catch (err: any) {
      goToPage(RouteName.VaultDetailGuest, { id: vaultId });
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchVault();
  }, [id]);

  useEffect(() => {
    if (vault && vault.id !== -1) {
      dispatch({
        type: appActions.SET_CURRENT_VAULT,
        currentVault: vault,
      });
    }

    return () => {
      dispatch({
        type: appActions.SET_CURRENT_VAULT,
        currentVault: undefined,
      });
    };
  }, [vault, dispatch]);

  const refetchVault = async () => {
    await fetchVault();
  };

  if (fetching) {
    return (
      <div className={styles.page}>
        <Spinner />
      </div>
    );
  }

  if (!vault) {
    return null;
  }

  return (
    <div className={styles.page}>
      <VaultTopBar
        vault={vault}
        breadcrumb={vaultSubPageBreadcrumb(Number(id), 'Settings')}
      />
      <div className={styles.content}>
        <SettingsLayout
          activeTab="vault"
          nav={<VaultSettingsNav currentVaultId={vault.id} />}
        >
          <AboutVault vault={vault} refetchVault={refetchVault} />
        </SettingsLayout>
      </div>
    </div>
  );
};

// The top bar's search / "New" menu need the vault detail context.
const VaultSettings: React.FC = () => (
  <VaultDetailProvider>
    <VaultSettingsContent />
  </VaultDetailProvider>
);

export { VaultSettings };
